import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useDevice } from '@/hooks/useDeviceType';
import { useActiveEvent, useActiveEventWithSponsors, formatEventDateRange, getTodaysAdSponsor } from '@/hooks/useActiveEvent';
import { useSocket } from '@/hooks/useSocket';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  Briefcase,
  MessageCircle,
  Users,
  Lightbulb,
  Map,
  User,
  Settings as SettingsIcon,
  HelpCircle,
  Shield,
  ClipboardCheck,
  Loader2,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BottomNav } from '@/components/navigation/BottomNav';
import { NotificationBellTrigger } from '@/components/NotificationBellTrigger';
import QRCodeBadge from '@/components/QRCodeBadge';
import ThemeToggle from '@/components/ThemeToggle';
import OnboardingFlow, { type WalkInProfileData } from '@/components/OnboardingFlow';
import { OfflineDataBanner } from '@/components/OfflineDataBanner';
import { AthenaMobileCard } from '@/components/AthenaDisclaimer';

// Lazy load desktop version and sponsor ad popup
const DashboardDesktopPage = lazy(() => import('./DashboardPage.desktop'));
const SponsorAdPopup = lazy(() => import('@/components/SponsorAdPopup'));

// Loading fallback for desktop
function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

export default function DashboardPage() {
  const { isDesktop } = useDevice();

  // Render desktop version for desktop users
  if (isDesktop) {
    return (
      <Suspense fallback={<DashboardSkeleton />}>
        <DashboardDesktopPage />
      </Suspense>
    );
  }

  // Mobile/Tablet version continues below
  return <DashboardMobilePage />;
}

function DashboardMobilePage() {
  const { user, updateUser } = useAuth();
  const { event: activeEvent } = useActiveEvent();
  const { event: eventWithSponsors } = useActiveEventWithSponsors();
  const { socket, isConnected } = useSocket();
  const todaysAdSponsor = getTodaysAdSponsor(eventWithSponsors?.sponsors);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [pushedAd, setPushedAd] = useState<{ sponsorName: string; adImageUrl: string } | null>(null);

  // Listen for admin-pushed test ads
  useEffect(() => {
    if (!socket || !isConnected) return;
    const onSponsorAd = (data: { sponsorName: string; adImageUrl: string }) => {
      setPushedAd(data);
    };
    socket.on('sponsor_ad', onSponsorAd);
    return () => { socket.off('sponsor_ad', onSponsorAd); };
  }, [socket, isConnected]);

  // Auto-show onboarding for users who haven't completed it
  useEffect(() => {
    if (user && user.onboardingCompleted === false) {
      setShowOnboarding(true);
    }
  }, [user]);

  const handleOnboardingComplete = useCallback(async (profileData?: WalkInProfileData) => {
    setShowOnboarding(false);

    try {
      // If walk-in user saved profile data, update profile first
      if (profileData) {
        const bio = profileData.bio?.trim() || null;
        let linkedinUrl = profileData.linkedinUrl?.trim() || '';
        let websiteUrl = profileData.websiteUrl?.trim() || '';
        if (linkedinUrl && !/^https?:\/\//i.test(linkedinUrl)) {
          linkedinUrl = `https://${linkedinUrl}`;
        }
        if (websiteUrl && !/^https?:\/\//i.test(websiteUrl)) {
          websiteUrl = `https://${websiteUrl}`;
        }

        await api.patch('/users/me', {
          bio,
          linkedinUrl: linkedinUrl || null,
          websiteUrl: websiteUrl || null,
        });

        // Mark onboarding as completed in the backend
        await api.patch('/users/me/onboarding', {
          onboardingStep: 3,
          onboardingCompleted: true,
        });

        // Batch state update to avoid useEffect re-triggering onboarding
        updateUser({
          bio,
          linkedinUrl: linkedinUrl || null,
          websiteUrl: websiteUrl || null,
          onboardingCompleted: true,
        });
        return;
      }

      // Mark onboarding as completed in the backend
      await api.patch('/users/me/onboarding', {
        onboardingStep: 3,
        onboardingCompleted: true,
      });
      // Update local user state
      updateUser({ onboardingCompleted: true });
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    }
  }, [updateUser]);

  const isAdmin = user?.roles?.includes('admin') || false;
  const isStaffOrAdmin = user?.roles?.includes('staff') || user?.roles?.includes('admin') || false;
  const unreadCount = 0; // TODO: Implement unread count

  const profileData = user ? {
    name: `${user.firstName} ${user.lastName}`.trim() || 'User',
    displayName: user.firstName || 'User',
    role: 'Attendee', // TODO: Get from user profile
    uuid: user.id,
    event: "SC-JAN26",
    org: '', // TODO: Get from user profile
    bio: '', // TODO: Get from user profile
    interests: [], // TODO: Get from user profile
    linkedin: '', // TODO: Get from user profile
    show_organization: true,
    allow_qr_scan: true
  } : {
    name: "User",
    displayName: "User",
    role: "Attendee",
    uuid: "GUEST",
    event: "SC-JAN26"
  };

  const quickActions = [
    {
      title: "Event Schedule",
      description: "Sessions & Workshops",
      icon: Calendar,
      href: "/schedule",
      color: "from-primary to-navy-light"
    },
    {
      title: "Event Map",
      description: "Campus & Venue Map",
      icon: Map,
      href: "/event-map",
      color: "from-primary to-tech-cyan"
    },
    {
      title: "Collaborative Opportunities",
      description: "Projects & Research",
      icon: Lightbulb,
      href: "/opportunities",
      color: "from-accent to-primary"
    },
    {
      title: "Industry Partnerships",
      description: "Our partners",
      icon: Briefcase,
      href: "/industry-partners",
      color: "from-navy-light to-tech-cyan"
    },
    {
      title: "My Network",
      description: "Connections & Invites",
      icon: Users,
      href: "/network",
      color: "from-military-green to-primary"
    },
    {
      title: "Messages",
      description: "Brainstorm, Explore, Commit",
      icon: MessageCircle,
      href: "/messages",
      color: "from-tech-cyan to-accent"
    },
    // Athena card is inserted here via AthenaMobileCard in the grid render
    ...(isStaffOrAdmin ? [{
      title: "Staff Check-In",
      description: "Scan Attendees",
      icon: ClipboardCheck,
      href: "/staff/checkin",
      color: "from-military-green to-tech-cyan",
      isStaff: true
    }] : []),
    ...(isAdmin ? [{
      title: "Admin Dashboard",
      description: "System Overview",
      icon: Shield,
      href: "/admin",
      color: "from-destructive to-accent",
      isAdmin: true
    }] : [])
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle pb-20 md:pb-8">
      {/* Header */}
      <header className="bg-gradient-navy text-primary-foreground shadow-lg rounded-xl mx-3 md:mx-4 mt-3 md:mt-4">
        <div className="container mx-auto px-3 md:px-4 py-3 md:py-6">
          {/* Mobile Layout */}
          <div className="flex md:hidden flex-col gap-2">
            <div className="flex items-center gap-3">
              <img src="/nps-logo.png" alt="NPS Logo" className="h-14 w-auto" />
              <div>
                <h1 className="text-base font-bold tracking-tight dark:text-white">Converge @ NPS</h1>
                <p className="text-xs font-semibold text-nps-gold">Naval Postgraduate School</p>
                <p className="text-[10px] text-tech-cyan-light">
                  {activeEvent ? formatEventDateRange(activeEvent.dateStart, activeEvent.dateEnd) : ''}
                </p>
              </div>
            </div>
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center justify-center gap-1 pt-1 border-t border-white/10">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-primary-foreground hover:bg-primary/20"
                      onClick={() => setShowOnboarding(true)}
                    >
                      <HelpCircle className="h-5 w-5 text-white" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Help</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="h-9 w-9 flex items-center justify-center">
                      <ThemeToggle />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Theme</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="[&_button]:h-9 [&_button]:w-9 [&_button]:text-primary-foreground [&_button]:hover:bg-primary/20 [&_svg]:text-white">
                      <NotificationBellTrigger />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Notifications</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/settings">
                      <Button variant="ghost" size="icon" className="h-11 w-11 md:h-10 md:w-10 text-primary-foreground hover:bg-primary/20">
                        <SettingsIcon className="h-5 w-5 md:h-6 md:w-6" />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Settings</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/profile">
                      <Button variant="ghost" size="icon" className="h-11 w-11 md:h-10 md:w-10 text-primary-foreground hover:bg-primary/20">
                        <User className="h-5 w-5 md:h-6 md:w-6" />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Profile</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/nps-logo.png" alt="NPS Logo" className="h-24 w-auto" />
              <div>
                <h1 className="text-xl font-bold tracking-tight dark:text-white">Converge @ NPS</h1>
                <p className="text-base font-semibold text-nps-gold mt-0.5">Naval Postgraduate School</p>
                <p className="text-sm text-tech-cyan-light mt-0.5">
                  {activeEvent ? formatEventDateRange(activeEvent.dateStart, activeEvent.dateEnd) : ''}
                </p>
              </div>
            </div>
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-primary-foreground hover:bg-primary/20"
                      onClick={() => setShowOnboarding(true)}
                    >
                      <HelpCircle className="h-8 w-8 text-white" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Help & Tutorial</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <ThemeToggle />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Toggle Theme</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/messages" className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-primary-foreground hover:bg-primary/20"
                      >
                        <MessageCircle className="h-6 w-6 text-white" />
                        {unreadCount > 0 && (
                          <span className="absolute top-1 right-1 h-3 w-3 bg-destructive rounded-full border-2 border-nps-navy" />
                        )}
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Messages</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="[&_button]:text-primary-foreground [&_button]:hover:bg-primary/20 [&_svg]:text-white">
                      <NotificationBellTrigger />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Notifications</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/settings">
                      <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary/20">
                        <SettingsIcon className="h-6 w-6" />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Settings</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/profile">
                      <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary/20">
                        <User className="h-6 w-6" />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>My Profile</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-4 md:py-8 pb-4 md:pb-8 max-w-6xl">
        <OfflineDataBanner />
        {/* Welcome Banner - Compact on mobile */}
        <Card className="p-3 md:p-6 mb-4 md:mb-8 bg-gradient-to-br from-card to-accent/5 border-accent/30">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-lg md:text-2xl font-bold mb-1 md:mb-2">Welcome, {profileData.displayName}!</h2>
              <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 md:line-clamp-none">
                {activeEvent?.welcomeMessage || 'Discover opportunities, connect with partners, and collaborate at NPS.'}
              </p>
            </div>
          </div>
        </Card>

        {/* QR Code Badge */}
        <Card className="p-3 md:p-6 mb-4 md:mb-8 shadow-md">
          <QRCodeBadge user={profileData} isOffline={false} />
        </Card>

        {/* Quick Actions Grid - Compact cards on mobile */}
        <div className="mb-4 md:mb-8">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4 auto-rows-fr">
            {quickActions.map((action, index) => {
              // Insert Athena card before first staff/admin card (after Messages at index 5)
              const athenaPosition = 6;
              const isAfterAthena = index >= athenaPosition;
              const displayIndex = isAfterAthena ? index + 1 : index;
              return (
                <React.Fragment key={action.title}>
                  {index === athenaPosition && (
                    <div className="h-full">
                      <AthenaMobileCard index={athenaPosition} />
                    </div>
                  )}
                  <Link to={action.href} className="h-full">
                    <Card
                      className={`h-full min-h-[120px] md:min-h-[160px] p-3 md:p-6 flex flex-col hover:shadow-lg transition-all duration-300 border-border/50 hover:border-accent/50 cursor-pointer group hover:scale-[1.02] hover:-translate-y-1 active:scale-[0.98] active:translate-y-0 animate-fade-in ${action.isAdmin ? 'border-accent/50 bg-gradient-to-br from-accent/5 to-destructive/5' : ''} ${(action as any).isStaff ? 'border-military-green/50 bg-gradient-to-br from-military-green/5 to-tech-cyan/5' : ''}`}
                      style={{ animationDelay: `${displayIndex * 50}ms` }}
                    >
                      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shrink-0`}>
                        <action.icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
                      </div>
                      <div className="flex-1 flex flex-col">
                        <h3 className="font-semibold text-sm md:text-lg mb-1 flex items-center gap-1 md:gap-2 transition-colors duration-300 group-hover:text-accent leading-tight">
                          {action.title}
                          {action.isAdmin && <Shield className="h-3 w-3 md:h-4 md:w-4 text-accent shrink-0" />}
                          {(action as any).isStaff && <ClipboardCheck className="h-3 w-3 md:h-4 md:w-4 text-military-green shrink-0" />}
                        </h3>
                        <p className="text-xs md:text-sm text-muted-foreground transition-colors duration-300 line-clamp-1 md:line-clamp-none">{action.description}</p>
                      </div>
                      {action.isAdmin && (
                        <Badge className="mt-2 md:mt-3 bg-destructive text-xs w-fit">Admin</Badge>
                      )}
                      {(action as any).isStaff && (
                        <Badge className="mt-2 md:mt-3 bg-military-green text-xs w-fit">Staff</Badge>
                      )}
                    </Card>
                  </Link>
                </React.Fragment>
              );
            })}
            {/* Athena card at end if no staff/admin cards pushed it earlier */}
            {quickActions.length <= 6 && (
              <div className="h-full">
                <AthenaMobileCard index={6} />
              </div>
            )}
          </div>

        </div>

      </main>

      {/* Bottom Navigation for Mobile */}
      <BottomNav />

      {/* Onboarding Flow Modal */}
      <OnboardingFlow
        open={showOnboarding}
        onComplete={handleOnboardingComplete}
        isWalkIn={user?.isWalkIn}
      />

      {/* Sponsor Ad Popup — admin-pushed ad takes priority, then daily scheduled */}
      {pushedAd && !showOnboarding && (
        <Suspense fallback={null}>
          <SponsorAdPopup
            imageUrl={pushedAd.adImageUrl}

            forceOpen
            onClose={() => setPushedAd(null)}
          />
        </Suspense>
      )}
      {!pushedAd && todaysAdSponsor?.adImageUrl && !showOnboarding && (
        <Suspense fallback={null}>
          <SponsorAdPopup
            imageUrl={todaysAdSponsor.adImageUrl}

          />
        </Suspense>
      )}
    </div>
  );
}
